import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import OnboardingLayout from './OnboardingLayout';
import '../../../styles/components/doctor/DoctorOnboarding.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://neurolex-platform-9b4c40c0e2da.herokuapp.com/api';

// Local storage key for persisting form data
const STORAGE_KEY = 'doctorOnboardingFormData';
const STEP_STORAGE_KEY = 'doctorOnboardingCurrentStep';

const DoctorOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState(null);
  const [hasRestoredData, setHasRestoredData] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    firstName: '',
    lastName: '',
    email: '',
    personalContactNumber: '',
    clinicLocation: '',
    clinicContactNumber: '',
    
    // Step 2: Professional Information
    specialty: '',
    title: '',
    areasOfExpertise: '',
    experience: '',
    
    // Step 3: Availability
    availability: {
      monday: { available: false, slots: [] },
      tuesday: { available: false, slots: [] },
      wednesday: { available: false, slots: [] },
      thursday: { available: false, slots: [] },
      friday: { available: false, slots: [] },
      saturday: { available: false, slots: [] },
      sunday: { available: false, slots: [] }
    },
    appointmentTypes: {
      inPerson: false,
      telehealth: false
    },
    consultationFees: '',
    
    // Step 4: Credentials
    education: [],
    licenses: [],
    certifications: [],
    
    // Document uploads (these will store file names for reference only)
    licenseDocument: null,
    educationCertificate: null,
    additionalDocuments: []
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const loadSavedData = () => {
      try {
        // Load saved form data
        const savedFormData = localStorage.getItem(STORAGE_KEY);
        const savedStep = localStorage.getItem(STEP_STORAGE_KEY);
        
        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          // Only restore non-file data (files can't be persisted in localStorage)
          const { licenseDocument, educationCertificate, additionalDocuments, ...restoreableData } = parsedData;
          
          setFormData(prev => ({
            ...prev,
            ...restoreableData,
            // Keep file fields as null since we can't restore actual files
            licenseDocument: null,
            educationCertificate: null,
            additionalDocuments: []
          }));
          
          setHasRestoredData(true);
          
          // Show notification about restored data
          toast.info('Your previous progress has been restored!', {
            position: "top-right",
            autoClose: 3000,
          });
        }
        
        if (savedStep) {
          setCurrentStep(parseInt(savedStep));
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        // If there's an error, clear corrupted data
        clearSavedData();
      }
    };

    loadSavedData();
  }, []);

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
  if (hasRestoredData || Object.values(formData).some(value => 
    typeof value === 'string' ? value.trim() !== '' : 
    typeof value === 'object' && value !== null ? Object.keys(value).length > 0 : 
    false
  )) {
    try {
      // Save form data (excluding actual file objects)
      const dataToSave = {
        ...formData,
        // Save file names for reference, not the actual files
        licenseDocument: formData.licenseDocument ? { name: formData.licenseDocument.name } : null,
        educationCertificate: formData.educationCertificate ? { name: formData.educationCertificate.name } : null,
        additionalDocuments: formData.additionalDocuments.map(file => 
          file ? { name: file.name } : null
        )
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }
}, [formData, hasRestoredData]);

  // Save current step to localStorage
  useEffect(() => {
    localStorage.setItem(STEP_STORAGE_KEY, currentStep.toString());
  }, [currentStep]);

  // Clear saved data from localStorage
  const clearSavedData = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STEP_STORAGE_KEY);
  };

  // Get current user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          const user = response.data.user;
          setUserData(user);
          
          // Only pre-fill if we haven't restored saved data
          if (!hasRestoredData) {
            setFormData(prev => ({
              ...prev,
              firstName: user.firstName || prev.firstName,
              lastName: user.lastName || prev.lastName,
              email: user.email || prev.email,
              personalContactNumber: user.personalContactNumber || prev.personalContactNumber,
              clinicLocation: user.clinicLocation || prev.clinicLocation,
              clinicContactNumber: user.clinicContactNumber || prev.clinicContactNumber,
              specialty: user.specialty || prev.specialty,
              title: user.title || prev.title,
              areasOfExpertise: user.areasOfExpertise || prev.areasOfExpertise,
              experience: user.experience || prev.experience,
              consultationFees: user.consultationFees || prev.consultationFees,
              availability: user.availability || prev.availability,
              appointmentTypes: user.appointmentTypes || prev.appointmentTypes,
              education: user.education || prev.education,
              licenses: user.licenses || prev.licenses,
              certifications: user.certifications || prev.certifications
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      }
    };

    fetchUserData();
  }, [navigate, hasRestoredData]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name.includes('appointmentTypes.')) {
        const appointmentType = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          appointmentTypes: {
            ...prev.appointmentTypes,
            [appointmentType]: checked
          }
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle availability changes
  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  // Add time slot
  const addTimeSlot = (day) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          slots: [...prev.availability[day].slots, { startTime: '', endTime: '' }]
        }
      }
    }));
  };

  // Handle time slot changes
  const handleTimeSlotChange = (day, slotIndex, field, value) => {
    setFormData(prev => {
      const newSlots = [...prev.availability[day].slots];
      newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]: {
            ...prev.availability[day],
            slots: newSlots
          }
        }
      };
    });
  };

  // Remove time slot
  const removeTimeSlot = (day, slotIndex) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          slots: prev.availability[day].slots.filter((_, index) => index !== slotIndex)
        }
      }
    }));
  };

  // Add credential entry
  const addCredential = (type) => {
    const newCredential = {
      id: Date.now(),
      institution: '',
      degree: '',
      year: '',
      licenseNumber: '',
      issuingAuthority: '',
      expirationDate: ''
    };
    
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], newCredential]
    }));
  };

  // Handle credential changes
  const handleCredentialChange = (type, id, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  // Remove credential
  const removeCredential = (type, id) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item.id !== id)
    }));
  };

  // Validation for each step
  const validateStep = (step) => {
    const errors = [];
    
    if (step === 1) {
      // Personal Information validation
      if (!formData.firstName) errors.push('First name is required');
      if (!formData.lastName) errors.push('Last name is required');
      if (!formData.email) errors.push('Email is required');
    }
    
    if (step === 2) {
      // Professional Information validation
      if (!formData.specialty) errors.push('Specialty is required');
      if (!formData.experience) errors.push('Experience is required');
    }
    
    if (step === 3) {
      // Availability validation
      const hasAvailability = Object.values(formData.availability).some(day => day.available);
      if (!hasAvailability) errors.push('Please select at least one day of availability');
      
      const hasAppointmentType = formData.appointmentTypes.inPerson || formData.appointmentTypes.telehealth;
      if (!hasAppointmentType) errors.push('Please select at least one appointment type');
    }
    
    if (step === 4) {
      // Credentials validation - make it optional but recommend
      if (formData.education.length === 0 && formData.licenses.length === 0) {
        toast.warning('Adding credentials will help verify your professional status and build patient trust');
      }
    }
    
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return false;
    }
    
    return true;
  };

  // Handle step navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle form submission
  // Fixed handleSubmit function for DoctorOnboarding.js
const handleSubmit = async () => {
  setIsSubmitting(true);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Create FormData for file uploads
    const submitData = new FormData();
    
    // FIXED: Flatten the nested appointmentTypes structure
    submitData.append('telehealth', formData.appointmentTypes.telehealth);
    submitData.append('inPerson', formData.appointmentTypes.inPerson);
    
    // FIXED: Fix the consultationFee field name (singular instead of plural)
    if (formData.consultationFees) {
      submitData.append('consultationFee', formData.consultationFees);
    }
    
    // Append other form fields (excluding the ones we've handled separately)
    Object.keys(formData).forEach(key => {
      if (!['licenseDocument', 'educationCertificate', 'additionalDocuments', 'appointmentTypes', 'consultationFees'].includes(key)) {
        if (typeof formData[key] === 'object' && formData[key] !== null) {
          // For other nested objects (e.g. availability), still stringify them
          submitData.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      }
    });

    // Append file fields
    if (formData.licenseDocument) {
      submitData.append('licenseDocument', formData.licenseDocument);
    }
    
    if (formData.educationCertificate) {
      submitData.append('educationCertificate', formData.educationCertificate);
    }
    
    if (formData.additionalDocuments && formData.additionalDocuments.length > 0) {
      formData.additionalDocuments.forEach((file) => {
        if (file) {
          submitData.append('additionalDocuments', file);
        }
      });
    }

    // Log the data being sent (for debugging)
    console.log('Submitting data:', Object.fromEntries(submitData.entries()));

    const response = await axios.put(`${API_URL}/auth/complete-profile`, submitData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.data.success) {
      // Clear saved data after successful submission
      clearSavedData();
      
      toast.success('Profile completed successfully! Your credentials will be reviewed by our team.');
      navigate('/doctor');
    }
  } catch (error) {
    console.error('Error updating profile:', error);
    toast.error(error.response?.data?.message || 'Failed to update profile');
  } finally {
    setIsSubmitting(false);
  }
};

  // Skip onboarding
  const handleSkip = () => {
    // Ask user if they want to save their progress
    if (window.confirm('Do you want to save your current progress? You can continue later from where you left off.')) {
      toast.info('Your progress has been saved. You can continue anytime from your dashboard.');
    } else {
      clearSavedData();
      toast.info('You can complete your profile anytime from your dashboard');
    }
    navigate('/doctor');
  };

  // Clear progress function (can be called manually)
  const handleClearProgress = () => {
    if (window.confirm('Are you sure you want to clear all your progress? This action cannot be undone.')) {
      clearSavedData();
      
      // Reset form to initial state
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        personalContactNumber: '',
        clinicLocation: '',
        clinicContactNumber: '',
        specialty: '',
        title: '',
        areasOfExpertise: '',
        experience: '',
        availability: {
          monday: { available: false, slots: [] },
          tuesday: { available: false, slots: [] },
          wednesday: { available: false, slots: [] },
          thursday: { available: false, slots: [] },
          friday: { available: false, slots: [] },
          saturday: { available: false, slots: [] },
          sunday: { available: false, slots: [] }
        },
        appointmentTypes: {
          inPerson: false,
          telehealth: false
        },
        consultationFees: '',
        education: [],
        licenses: [],
        certifications: [],
        licenseDocument: null,
        educationCertificate: null,
        additionalDocuments: []
      });
      
      setCurrentStep(1);
      setHasRestoredData(false);
      
      toast.success('All progress has been cleared. Starting fresh!');
    }
  };

// Render Step 1: Personal Information
const renderStepOne = () => (
  <div className="onboarding-doctor-personal-info-step">
    <div className="onboarding-doctor-personal-step-header">
      <h2>Personal Information</h2>
    </div>

    <div className="onboarding-doctor-personal-form-grid">
      <div className="onboarding-doctor-personal-field-group">
        <label htmlFor="firstName">First Name *</label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          className="onboarding-doctor-personal-input-field"
          placeholder="Enter your first name"
          required
        />
      </div>

      <div className="onboarding-doctor-personal-field-group">
        <label htmlFor="lastName">Last Name *</label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          className="onboarding-doctor-personal-input-field"
          placeholder="Enter your last name"
          required
        />
      </div>

      <div className="onboarding-doctor-personal-field-group">
        <label htmlFor="personalContactNumber">Personal Contact Number</label>
        <input
          type="tel"
          id="personalContactNumber"
          name="personalContactNumber"
          value={formData.personalContactNumber}
          onChange={handleChange}
          className="onboarding-doctor-personal-input-field"
          placeholder="Your personal phone number"
        />
      </div>

      <div className="onboarding-doctor-personal-field-group">
        <label htmlFor="clinicLocation">Clinic/Practice Location</label>
        <input
          type="text"
          id="clinicLocation"
          name="clinicLocation"
          value={formData.clinicLocation}
          onChange={handleChange}
          className="onboarding-doctor-personal-input-field"
          placeholder="Your practice location"
        />
      </div>

      <div className="onboarding-doctor-personal-field-group">
        <label htmlFor="clinicContactNumber">Clinic Contact Number</label>
        <input
          type="tel"
          id="clinicContactNumber"
          name="clinicContactNumber"
          value={formData.clinicContactNumber}
          onChange={handleChange}
          className="onboarding-doctor-personal-input-field"
          placeholder="Clinic phone number"
        />
      </div>

      <div className="onboarding-doctor-personal-field-group">
        <label htmlFor="email">Email Address *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="onboarding-doctor-personal-input-field"
          placeholder="Enter your email address"
          required
          disabled={userData?.email}
        />
        {userData?.email && (
          <small className="onboarding-doctor-personal-field-hint">Email cannot be changed after registration</small>
        )}
      </div>
    </div>

    <div className="onboarding-doctor-personal-step-actions">
      <button 
        type="button" 
        onClick={handleSkip}
        className="onboarding-doctor-personal-btn-skip"
      >
        Skip for now
      </button>
      <button 
        type="button" 
        onClick={nextStep}
        className="onboarding-doctor-personal-btn-primary"
      >
        Continue
      </button>
    </div>
  </div>
);

// Render Step 2: Professional Information
const renderStepTwo = () => (
  <div className="onboarding-doctor-professional-info-step">
    <div className="onboarding-doctor-professional-step-header">
      <h2>Professional Information</h2>
    </div>

    <div className="onboarding-doctor-professional-form-grid">
      <div className="onboarding-doctor-professional-field-group">
        <label htmlFor="specialty">Medical Specialty *</label>
        <select
          id="specialty"
          name="specialty"
          value={formData.specialty}
          onChange={handleChange}
          className="onboarding-doctor-professional-select-field"
          required
        >
          <option value="">Select your specialization</option>
          <option value="Psychiatrist">Psychiatrist</option>
          <option value="Psychologist">Psychologist</option>
          <option value="Clinical Psychologist">Clinical Psychologist</option>
          <option value="Therapist">Therapist</option>
          <option value="Counselor">Counselor</option>
          <option value="Social Worker">Social Worker</option>
          <option value="Psychiatric Nurse">Psychiatric Nurse</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="onboarding-doctor-professional-field-group">
        <label htmlFor="title">Professional Title</label>
        <select
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="onboarding-doctor-professional-select-field"
        >
          <option value="">Select a title</option>
          <option value="Dr.">Dr.</option>
          <option value="Professor">Professor</option>
          <option value="Therapist">Therapist</option>
          <option value="Counselor">Counselor</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="onboarding-doctor-professional-field-group">
        <label htmlFor="experience">Years of Experience *</label>
        <input
          type="text"
          id="experience"
          name="experience"
          value={formData.experience}
          onChange={handleChange}
          className="onboarding-doctor-professional-input-field"
          placeholder="e.g., 5 years, 10+ years"
          required
        />
      </div>

      {/* Empty div to balance the grid */}
      <div></div>

      <div className="onboarding-doctor-professional-field-group onboarding-doctor-professional-full-width">
        <label htmlFor="areasOfExpertise">Areas of Expertise</label>
        <input
          type="text"
          id="areasOfExpertise"
          name="areasOfExpertise"
          value={formData.areasOfExpertise}
          onChange={handleChange}
          className="onboarding-doctor-professional-input-field"
          placeholder="e.g., Anxiety Disorders, Depression, PTSD, Trauma Therapy"
        />
        <small className="onboarding-doctor-professional-field-hint">Separate multiple areas with commas</small>
      </div>
    </div>

    <div className="onboarding-doctor-professional-step-actions">
      <button 
        type="button" 
        onClick={prevStep}
        className="onboarding-doctor-professional-btn-secondary"
      >
        Back
      </button>
      <button 
        type="button" 
        onClick={nextStep}
        className="onboarding-doctor-professional-btn-primary"
      >
        Continue
      </button>
    </div>
  </div>
);

// Render Step 3: Availability
const renderStepThree = () => (
  <div className="onboarding-doctor-availability-step">
    <div className="onboarding-doctor-availability-step-header">
      <h2>Availability & Services</h2>
    </div>

    {/* Availability Section */}
    <div className="onboarding-doctor-availability-form-section">
      <h3>Weekly Availability *</h3>
      <p className="onboarding-doctor-availability-section-description">Select days you're available and set specific hours for each day</p>
      <div className="onboarding-doctor-availability-days-grid">
        {Object.keys(formData.availability).map(day => (
          <div key={day} className="onboarding-doctor-day-availability-card">
            <div className="onboarding-doctor-day-header">
              <input
                type="checkbox"
                id={`${day}-available`}
                checked={formData.availability[day].available}
                onChange={(e) => handleAvailabilityChange(day, 'available', e.target.checked)}
                className="onboarding-doctor-day-checkbox"
              />
              <label htmlFor={`${day}-available`} className="onboarding-doctor-day-label">
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </label>
            </div>
            
            {formData.availability[day].available && (
              <div className="onboarding-doctor-time-slots-container">
                {formData.availability[day].slots.map((slot, index) => (
                  <div key={index} className="onboarding-doctor-time-slot-row">
                    <input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) => handleTimeSlotChange(day, index, 'startTime', e.target.value)}
                      className="onboarding-doctor-time-input"
                      placeholder="Start time"
                    />
                    <span className="onboarding-doctor-time-separator">to</span>
                    <input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) => handleTimeSlotChange(day, index, 'endTime', e.target.value)}
                      className="onboarding-doctor-time-input"
                      placeholder="End time"
                    />
                    <button
                      type="button"
                      onClick={() => removeTimeSlot(day, index)}
                      className="onboarding-doctor-btn-remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addTimeSlot(day)}
                  className="onboarding-doctor-btn-add-slot"
                >
                  + Add Time Slot
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Services Section */}
    <div className="onboarding-doctor-services-form-section">
      <h3>Appointment Types *</h3>
      <div className="onboarding-doctor-appointment-types">
        <div className="onboarding-doctor-appointment-option">
          <input
            type="checkbox"
            id="inPerson"
            name="appointmentTypes.inPerson"
            checked={formData.appointmentTypes.inPerson}
            onChange={handleChange}
            className="onboarding-doctor-appointment-checkbox"
          />
          <label htmlFor="inPerson" className="onboarding-doctor-appointment-label">
            <span className="onboarding-doctor-option-title">In-Person Consultations</span>
            <span className="onboarding-doctor-option-description">Face-to-face appointments at your clinic</span>
          </label>
        </div>
        <div className="onboarding-doctor-appointment-option">
          <input
            type="checkbox"
            id="telehealth"
            name="appointmentTypes.telehealth"
            checked={formData.appointmentTypes.telehealth}
            onChange={handleChange}
            className="onboarding-doctor-appointment-checkbox"
          />
          <label htmlFor="telehealth" className="onboarding-doctor-appointment-label">
            <span className="onboarding-doctor-option-title">Telehealth/Video Calls</span>
            <span className="onboarding-doctor-option-description">Online video consultations</span>
          </label>
        </div>
      </div>

      <div className="onboarding-doctor-fees-field-group">
        <label htmlFor="consultationFee">Consultation Fees (Optional)</label>
        <input
          type="text"
          id="consultationFee"
          name="consultationFee" 
          value={formData.consultationFee} 
          onChange={handleChange}
          className="onboarding-doctor-fees-input-field"
          placeholder="e.g., ₱1,500 per session"
        />
      </div>
    </div>

    <div className="onboarding-doctor-availability-step-actions">
      <button 
        type="button" 
        onClick={prevStep}
        className="onboarding-doctor-availability-btn-secondary"
      >
        Back
      </button>
      <button 
        type="button" 
        onClick={handleSkip}
        className="onboarding-doctor-availability-btn-skip"
      >
        Skip for now
      </button>
      <button 
        type="button" 
        onClick={nextStep}
        className="onboarding-doctor-availability-btn-primary"
      >
        Continue
      </button>
    </div>
  </div>
);

// Render Step 4: Credentials & Documents
const renderStepFour = () => (
  <div className="onboarding-doctor-credentials-step">
    <div className="onboarding-doctor-credentials-step-header">
      <h2>Credentials & Documents</h2>
      <p>Upload your credentials to verify your professional status</p>
    </div>

    {/* Education Section with File Upload */}
    <div className="onboarding-doctor-education-form-section">
      <h3>Education</h3>
      
      <div className="onboarding-doctor-credentials-section">
        {formData.education.map((edu) => (
          <div key={edu.id} className="onboarding-doctor-credential-item">
            <input
              type="text"
              placeholder="Degree / Qualification"
              value={edu.degree}
              onChange={(e) => handleCredentialChange('education', edu.id, 'degree', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <input
              type="text"
              placeholder="Institution"
              value={edu.institution}
              onChange={(e) => handleCredentialChange('education', edu.id, 'institution', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <input
              type="number"
              placeholder="Year"
              value={edu.year}
              onChange={(e) => handleCredentialChange('education', edu.id, 'year', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <button
              type="button"
              onClick={() => removeCredential('education', edu.id)}
              className="onboarding-doctor-btn-remove"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addCredential('education')}
          className="onboarding-doctor-btn-add-credential"
        >
          + Add Education
        </button>
      </div>

      {/* Education Certificate Upload - Below the inputs */}
      <div className="onboarding-doctor-upload-item">
        <label className="onboarding-doctor-upload-label">Upload Education Certificate</label>
        <div className="onboarding-doctor-file-upload-area">
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => setFormData(prev => ({ ...prev, educationCertificate: e.target.files[0] }))}
            className="onboarding-doctor-file-input"
            id="educationCertificate"
          />
          <label htmlFor="educationCertificate" className="onboarding-doctor-file-upload-button">
            Choose File
          </label>
          <span className="onboarding-doctor-file-name">
            {formData.educationCertificate ? formData.educationCertificate.name : 'No file chosen'}
          </span>
        </div>
      </div>
    </div>

    {/* Licenses Section with File Upload */}
    <div className="onboarding-doctor-licenses-form-section">
      <h3>Professional Licenses</h3>
      
      <div className="onboarding-doctor-credentials-section">
        {formData.licenses.map((license) => (
          <div key={license.id} className="onboarding-doctor-credential-item">
            <input
              type="text"
              placeholder="License Type"
              value={license.degree}
              onChange={(e) => handleCredentialChange('licenses', license.id, 'degree', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <input
              type="text"
              placeholder="License Number"
              value={license.licenseNumber}
              onChange={(e) => handleCredentialChange('licenses', license.id, 'licenseNumber', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <input
              type="date"
              placeholder="Expiration Date"
              value={license.expirationDate}
              onChange={(e) => handleCredentialChange('licenses', license.id, 'expirationDate', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <button
              type="button"
              onClick={() => removeCredential('licenses', license.id)}
              className="onboarding-doctor-btn-remove"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addCredential('licenses')}
          className="onboarding-doctor-btn-add-credential"
        >
          + Add License
        </button>
      </div>

      {/* License Document Upload - Below the inputs */}
      <div className="onboarding-doctor-upload-item">
        <label className="onboarding-doctor-upload-label">Upload License Document</label>
        <div className="onboarding-doctor-file-upload-area">
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => setFormData(prev => ({ ...prev, licenseDocument: e.target.files[0] }))}
            className="onboarding-doctor-file-input"
            id="licenseDocument"
          />
          <label htmlFor="licenseDocument" className="onboarding-doctor-file-upload-button">
            Choose File
          </label>
          <span className="onboarding-doctor-file-name">
            {formData.licenseDocument ? formData.licenseDocument.name : 'No file chosen'}
          </span>
        </div>
      </div>
    </div>

    {/* Certifications Section */}
    <div className="onboarding-doctor-certifications-form-section">
      <h3>Certifications</h3>
      <div className="onboarding-doctor-credentials-section">
        {formData.certifications.map((cert) => (
          <div key={cert.id} className="onboarding-doctor-credential-item">
            <input
              type="text"
              placeholder="Certification Name"
              value={cert.degree}
              onChange={(e) => handleCredentialChange('certifications', cert.id, 'degree', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <input
              type="text"
              placeholder="Issuing Authority"
              value={cert.issuingAuthority}
              onChange={(e) => handleCredentialChange('certifications', cert.id, 'issuingAuthority', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <input
              type="number"
              placeholder="Year"
              value={cert.year}
              onChange={(e) => handleCredentialChange('certifications', cert.id, 'year', e.target.value)}
              className="onboarding-doctor-credential-input"
            />
            <button
              type="button"
              onClick={() => removeCredential('certifications', cert.id)}
              className="onboarding-doctor-btn-remove"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addCredential('certifications')}
          className="onboarding-doctor-btn-add-credential"
        >
          + Add Certification
        </button>
      </div>
    </div>

    {/* Additional Documents Section */}
    <div className="onboarding-doctor-documents-form-section">
      <h3>Additional Documents</h3>
      <small className="onboarding-doctor-upload-hint">You can upload additional supporting documents</small>
      <div className="onboarding-doctor-additional-documents">
        {formData.additionalDocuments.map((doc, index) => (
          <div key={index} className="onboarding-doctor-file-upload-area">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => {
                const newDocs = [...formData.additionalDocuments];
                newDocs[index] = e.target.files[0];
                setFormData(prev => ({ ...prev, additionalDocuments: newDocs }));
              }}
              className="onboarding-doctor-file-input"
              id={`additionalDoc-${index}`}
            />
            <label htmlFor={`additionalDoc-${index}`} className="onboarding-doctor-file-upload-button">
              Choose File
            </label>
            <span className="onboarding-doctor-file-name">
              {doc ? doc.name : 'No file chosen'}
            </span>
            <button
              type="button"
              onClick={() => {
                const newDocs = formData.additionalDocuments.filter((_, i) => i !== index);
                setFormData(prev => ({ ...prev, additionalDocuments: newDocs }));
              }}
              className="onboarding-doctor-btn-remove"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            setFormData(prev => ({ 
              ...prev, 
              additionalDocuments: [...prev.additionalDocuments, null] 
            }));
          }}
          className="onboarding-doctor-btn-add-file"
        >
          + Add Another Document
        </button>
      </div>
    </div>

    {/* Verification Notice */}
    <div className="onboarding-doctor-verification-notice">
      <div className="onboarding-doctor-notice-icon">ⓘ</div>
      <div className="onboarding-doctor-notice-content">
        <strong>Verification Process:</strong> Your application will be reviewed by our administrative team.
        You will receive an email notification once your professional status has been verified.
        This process typically takes 1-3 business days.
      </div>
    </div>

    <div className="onboarding-doctor-credentials-step-actions">
      <button 
        type="button" 
        onClick={prevStep}
        className="onboarding-doctor-credentials-btn-secondary"
      >
        Back
      </button>
      <button 
        type="button" 
        onClick={handleSkip}
        className="onboarding-doctor-credentials-btn-skip"
      >
        Skip for now
      </button>
      <button 
        type="button" 
        onClick={nextStep}
        className="onboarding-doctor-credentials-btn-primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Saving...' : 'Complete Profile'}
      </button>
    </div>
  </div>
);

  return (
    <OnboardingLayout currentStep={currentStep} totalSteps={4} canSkip={true}>
      <div className="doc-onboard-container">
        {/* Progress indicator */}

        {/* Step Content */}
        {currentStep === 1 && renderStepOne()}
        {currentStep === 2 && renderStepTwo()}
        {currentStep === 3 && renderStepThree()}
        {currentStep === 4 && renderStepFour()}
      </div>
    </OnboardingLayout>
  );
};

export default DoctorOnboarding;